import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReservationsCalendar from '../components/ReservationsCalendar';
import MassCancellationDialog from '../components/MassCancellationDialog';
import {
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  Paper,
  Fade,
  Zoom,
  Tabs,
  Tab,
  Snackbar,
  Divider
} from '@mui/material';
import {
  Add,
  ArrowBack,
  Hotel,
  Restaurant,
  ContentCut,
  SportsSoccer,
  Build,
  Star,
  TrendingUp,
  People,
  EventSeat,
  Cancel,
  Pets,
  Delete
} from '@mui/icons-material';
import axios from 'axios';
import CatLoadingSpinner from '../components/CatLoadingSpinner';
import { useThemeMode } from '../contexts/ThemeContext';
import BusinessTour from '../components/MichiTour/BusinessTour';
import LynaAvatar from '../components/LynaAvatar';
import '../cat-theme.css';

interface Business {
  id: string;
  name: string;
  description: string;
  slug: string;
}

interface Board {
  id: string;
  name: string;
  type: string;
  description: string;
  capacity: number;
  slug: string;
}

interface Reservation {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  board: {
    id: string;
    name: string;
    type: string;
  };
}
const typeOptions = [
  // Usamos 'sports_fields' del backend
  { value: 'sports_fields', label: 'Cancha ⚽' },
  // Usamos 'salons' del backend
  { value: 'salons', label: 'Salón / Mesa 🍽️' },
  // Desglose de las opciones de hospedaje para más precisión
  { value: 'rooms', label: 'Habitación 🛏️' },
  { value: 'cabins', label: 'Cabaña 🛖' },
  { value: 'camping', label: 'Camping / Parcela ⛺' },
  // Usamos 'appointments' del backend
  { value: 'appointments', label: 'Servicio / Cita 💇' },
  // Agregamos el nuevo tipo 'equipment' del backend
  { value: 'equipment', label: 'Alquiler de Equipo ⚙️' },
];


const BusinessDetail: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [boards, setBoards] = useState<Board[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [massCancelOpen, setMassCancelOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'business' | 'board', id: string } | null>(null);
  const navigate = useNavigate();
  const { mode: themeMode } = useThemeMode();
  const isDark = themeMode === 'dark';

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.turn.ar/';

  useEffect(() => {
    if (businessId) {
      fetchBusiness();
      fetchBoards();
      fetchReservations();
    }
  }, [businessId]);

  const fetchBusiness = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusiness(response.data);
    } catch (error) {
      console.error('Error fetching business:', error);
    }
  };

  const fetchBoards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/businesses/${businessId}/boards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(response.data);
    } catch (error) {
      console.error('Error fetching boards:', error);
    }
  };

  const fetchReservations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/businesses/${businessId}/reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReservations(response.data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  };

  const handleCreateBoard = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/businesses/${businessId}/boards`,
        {
          name,
          type,
          description,
          capacity: parseInt(capacity)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setOpen(false);
      fetchBoards();
      setName('');
      setType('');
      setDescription('');
      setCapacity('');
    } catch (error) {
      setError('Error al crear el tablero');
    }
  };

  const handleMassCancellation = async (startDate: Date, endDate: Date, boardIds: string[], reason: string, startTime?: string, endTime?: string) => {
    try {
      const token = localStorage.getItem('token');

      // Adjust dates to cover the full range
      const adjustedStartDate = new Date(startDate);
      adjustedStartDate.setHours(0, 0, 0, 0);

      const adjustedEndDate = new Date(endDate);
      adjustedEndDate.setHours(23, 59, 59, 999);

      const response = await axios.post(
        `${API_URL}/api/businesses/${businessId}/reservations/cancel-bulk`,
        {
          startDate: adjustedStartDate,
          endDate: adjustedEndDate,
          boardIds,
          reason,
          startTime,
          endTime
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbar({
        open: true,
        message: `Se han cancelado ${response.data.count || 0} reservas exitosamente`,
        severity: 'success'
      });

      // Refresh reservations after cancellation
      fetchReservations();
    } catch (error) {
      console.error('Error in mass cancellation:', error);
      setSnackbar({
        open: true,
        message: 'Error al cancelar las reservas',
        severity: 'error'
      });
      throw error;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cancha': return <SportsSoccer />;
      case 'mesa': return <Restaurant />;
      case 'habitacion': return <Hotel />;
      case 'servicio': return <ContentCut />;
      default: return <EventSeat />;
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      const token = localStorage.getItem('token');
      if (itemToDelete.type === 'business') {
        await axios.delete(`${API_URL}/api/businesses/${businessId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        navigate('/dashboard');
      } else {
        await axios.delete(`${API_URL}/api/businesses/${businessId}/boards/${itemToDelete.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchBoards();
      }
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      setSnackbar({ open: true, message: 'Elemento enviado a la papelera', severity: 'success' });
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Error al eliminar el elemento');
    }
  };

  if (!business) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CatLoadingSpinner message="Buscando tu negocio..." />
    </Box>
  );

  return (
    <Box className="cat-paw-background" sx={{ minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 4 },
            background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(155, 89, 182, 0.3)'
          }}
          data-tour="business-header"
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/dashboard')}
              sx={{ color: 'white', mb: { xs: 1, sm: 2 }, opacity: 0.9, fontSize: { xs: '0.875rem', sm: '1rem' }, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Volver al Dashboard
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 1, flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
              <Avatar
                sx={{
                  width: { xs: 48, sm: 64 },
                  height: { xs: 48, sm: 64 },
                  bgcolor: isDark ? '#1E1E1E' : 'white',
                  color: '#FF6B9D',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.5)' : '0 4px 15px rgba(0,0,0,0.2)',
                  animation: 'cat-bounce 2s infinite'
                }}
              >
                {business.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h3" component="h1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', textShadow: '0 2px 4px rgba(0,0,0,0.2)', fontSize: { xs: '1.75rem', sm: '3rem' } }}>
                  {business.name} <LynaAvatar size={48} sx={{ ml: 1, display: 'inline-flex' }} animationEnabled={false} />
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {business.description}
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Decorative paw */}
          <Box sx={{ position: 'absolute', right: -20, top: -20, fontSize: { xs: '100px', sm: '150px' }, opacity: 0.1, transform: 'rotate(15deg)' }}>
            🐾
          </Box>
        </Paper>

        {/* Tabs Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, bgcolor: themeMode === 'dark' ? 'rgba(30,30,30,0.8)' : 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', borderRadius: 2 }} data-tour="business-tabs">
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            textColor="secondary"
            indicatorColor="secondary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ '& .MuiTab-root': { fontWeight: 'bold' } }}
          >
            <Tab icon={<TrendingUp />} label="Resumen" iconPosition="start" data-tour="tab-resumen" />
            <Tab icon={<EventSeat />} label="Agendas" iconPosition="start" data-tour="tab-agendas" />
            <Tab icon={<Build />} label="Configuración" iconPosition="start" data-tour="tab-configuracion" />
            <Tab icon={<People />} label="Compartir" iconPosition="start" data-tour="tab-compartir" />
          </Tabs>
        </Box>

        {/* Tab 0: Resumen */}
        {currentTab === 0 && (
          <Fade in timeout={500}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" className="cat-gradient-text">
                  Vista General
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 3, mb: 4 }}>
                <Card className="cat-card" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EventSeat sx={{ color: '#FF6B9D' }} />
                      <Typography variant="h6" color="text.secondary">Agendas Activas</Typography>
                    </Box>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: '#9B59B6' }}>{boards.length}</Typography>
                  </CardContent>
                </Card>

                <Card className="cat-card" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <People sx={{ color: '#4ECDC4' }} />
                      <Typography variant="h6" color="text.secondary">Capacidad Total</Typography>
                    </Box>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: '#4ECDC4' }}>
                      {boards.reduce((acc, curr) => acc + curr.capacity, 0)}
                    </Typography>
                  </CardContent>
                </Card>

                <Card className="cat-card" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Star sx={{ color: '#FF9A56' }} />
                      <Typography variant="h6" color="text.secondary">Ocupación Media</Typography>
                    </Box>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: '#FF9A56' }}>--%</Typography>
                  </CardContent>
                </Card>
              </Box>


              {/* Calendar View */}
              <Paper sx={{ p: 2, borderRadius: 3, bgcolor: themeMode === 'dark' ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)' }}>
                <ReservationsCalendar
                  reservations={reservations}
                  boards={boards}
                  onReservationChange={fetchReservations}
                />
              </Paper>
            </Box>
          </Fade >
        )}

        {/* Tab 1: Agendas */}
        {currentTab === 1 && (
          <Fade in timeout={500}>
            <Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 2, sm: 3 }, gap: { xs: 2, sm: 0 } }}>
                <Typography variant="h5" fontWeight="bold" className="cat-gradient-text" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Mis Agendas
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setOpen(true)}
                  className="cat-button"
                  sx={{
                    background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
                    boxShadow: '0 4px 15px rgba(155, 89, 182, 0.4)',
                    borderRadius: 3,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                  data-tour="nueva-agenda"
                >
                  Nueva Agenda
                </Button>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: { xs: 2, sm: 3 } }}>
                {boards.map((board, index) => (
                  <Zoom in style={{ transitionDelay: `${index * 100}ms` }} key={board.id}>
                    <Card
                      className="cat-card"
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 3,
                        border: '1px solid rgba(155, 89, 182, 0.2)',
                        minWidth: { xs: '100%', sm: 280 }
                      }}
                      onClick={() => navigate(`/board/${board.id}`)}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 } }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Avatar sx={{ bgcolor: isDark ? '#2A1A20' : '#FFF5F8', color: '#FF6B9D', border: '1px solid #FF6B9D', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
                              {getTypeIcon(board.type)}
                            </Avatar>
                            <Chip
                              label={board.type.toUpperCase()}
                              size="small"
                              sx={{ bgcolor: isDark ? '#2A2040' : '#E8D5F2', color: '#9B59B6', fontWeight: 'bold', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            />
                          </Box>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setItemToDelete({ type: 'board', id: board.id });
                              setDeleteConfirmOpen(true);
                            }}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Eliminar
                          </Button>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: isDark ? '#E0E0E0' : '#2c3e50', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                          {board.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {board.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <People fontSize="small" sx={{ color: '#9B59B6' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Capacidad: {board.capacity} personas
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Zoom>
                ))}
              </Box>
            </Box>
          </Fade>
        )}

        {/* Tab 2: Configuración */}
        {currentTab === 2 && (
          <Fade in={true} timeout={500}>
            <Paper
              className="cat-card"
              elevation={0}
              sx={{
                p: { xs: 2, sm: 4 },
                background: themeMode === 'dark' ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 4,
                border: themeMode === 'dark' ? '1px solid rgba(60,60,60,0.3)' : '1px solid rgba(255,255,255,0.2)',
                mb: { xs: 2, sm: 4 }
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                className="cat-gradient-text"
                sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                Configuración del Negocio
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                <TextField
                  label="Nombre del Negocio"
                  value={business.name}
                  onChange={(e) => setBusiness({ ...business, name: e.target.value })}
                  fullWidth
                  variant="outlined"
                  sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                />
                <TextField
                  label="Descripción"
                  value={business.description}
                  onChange={(e) => setBusiness({ ...business, description: e.target.value })}
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                />
                <TextField
                  label="Slug (URL amigable)"
                  value={business.slug || ''}
                  onChange={(e) => setBusiness({ ...business, slug: e.target.value })}
                  fullWidth
                  variant="outlined"
                  helperText={`Tu negocio será accesible en: ${window.location.origin}/${business.slug || 'tu-negocio'}`}
                  sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } }, '& .MuiFormHelperText-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: 1, sm: 2 } }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(
                          `${API_URL}/api/businesses/${businessId}`,
                          {
                            name: business.name,
                            description: business.description,
                            slug: business.slug
                          },
                          {
                            headers: { Authorization: `Bearer ${token}` }
                          }
                        );
                        setError('');
                        alert('Configuración guardada exitosamente');
                      } catch (error: any) {
                        console.error('Error updating business:', error);
                        setError(error.response?.data?.message || 'Error al actualizar la configuración');
                      }
                    }}
                    className="cat-button"
                    sx={{
                      background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
                      boxShadow: '0 4px 15px rgba(155, 89, 182, 0.4)',
                      px: { xs: 3, sm: 4 },
                      py: { xs: 1, sm: 1.5 },
                      borderRadius: 3,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    Guardar Cambios
                  </Button>
                </Box>

                <Divider sx={{ my: { xs: 1, sm: 2 } }} />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, p: { xs: 1.5, sm: 2 }, bgcolor: isDark ? 'rgba(255,152,0,0.08)' : 'rgba(255,152,0,0.04)', borderRadius: 2, border: isDark ? '1px solid rgba(255,152,0,0.3)' : '1px solid rgba(255,152,0,0.2)', gap: { xs: 2, sm: 0 } }}>
                  <Box>
                    <Typography variant="h6" color="warning.main" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Cancelar Reservas en Lote</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Cancela múltiples reservas por rango de fechas. Útil para feriados o cierres.</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<Cancel />}
                    onClick={() => setMassCancelOpen(true)}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    data-tour="cancelar-turnos"
                  >
                    Cancelar Turnos
                  </Button>
                </Box>

                <Divider sx={{ my: { xs: 1, sm: 2 } }} />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, p: { xs: 1.5, sm: 2 }, bgcolor: isDark ? '#2A1A20' : '#FFF5F8', borderRadius: 2, border: isDark ? '1px solid #4A2030' : '1px solid #FFCDD2', gap: { xs: 2, sm: 0 } }}>
                  <Box>
                    <Typography variant="h6" color="error" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Zona de Peligro</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Eliminar este negocio y todos sus datos asociados.</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => {
                      setItemToDelete({ type: 'business', id: business.id });
                      setDeleteConfirmOpen(true);
                    }}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Eliminar Negocio
                  </Button>
                </Box>
                {error && (
                  <Alert severity="error" sx={{ mt: { xs: 1, sm: 2 } }}>
                    {error}
                  </Alert>
                )}
              </Box>
            </Paper>
          </Fade>
        )}

        {/* Tab 3: Compartir */}
        {currentTab === 3 && (
          <Fade in={true} timeout={500}>
            <Box>
              <Paper
                className="cat-card"
                elevation={0}
                sx={{
                  p: 4,
                  background: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.2)',
                  mb: 4
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  className="cat-gradient-text"
                  sx={{ mb: 3 }}
                >
                  Compartir tu Negocio
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Pets sx={{ color: '#FF6B9D' }} /> Enlace Principal
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      bgcolor: isDark ? '#2A1A20' : '#FFF5F8',
                      borderRadius: 2,
                      border: isDark ? '1px solid #4A2030' : '1px solid #FFCDD2'
                    }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: 'monospace',
                          flex: 1,
                          wordBreak: 'break-all',
                          color: '#9B59B6'
                        }}
                      >
                        {business.slug
                          ? `${window.location.origin}/${business.slug}`
                          : `${window.location.origin}/public/business/${business.id}`
                        }
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => {
                          const url = business.slug
                            ? `${window.location.origin}/${business.slug}`
                            : `${window.location.origin}/public/business/${business.id}`;
                          navigator.clipboard.writeText(url);
                        }}
                        sx={{ bgcolor: '#9B59B6', '&:hover': { bgcolor: '#8E44AD' } }}
                      >
                        Copiar
                      </Button>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Enlaces de Agendas
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {boards.map((board) => (
                        <Box key={board.id} sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          bgcolor: isDark ? '#1E1E1E' : 'white',
                          borderRadius: 2,
                          border: isDark ? '1px solid #444' : '1px solid #E0E0E0',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: '#FF6B9D', boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.05)' }
                        }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              flex: 1,
                              wordBreak: 'break-all'
                            }}
                          >
                            {business.slug && board.slug
                              ? `${window.location.origin}/${business.slug}/${board.slug}`
                              : `${window.location.origin}/public/board/${board.id}`
                            }
                          </Typography>
                          <Chip
                            label={board.type.replace('_', ' ').toUpperCase()}
                            size="small"
                            sx={{ bgcolor: isDark ? '#2A2040' : '#E8D5F2', color: '#9B59B6' }}
                            className="mr-2"
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const url = business.slug && board.slug
                                ? `${window.location.origin}/${business.slug}/${board.slug}`
                                : `${window.location.origin}/public/board/${board.id}`;
                              navigator.clipboard.writeText(url);
                            }}
                            sx={{ color: '#FF6B9D', borderColor: '#FF6B9D', '&:hover': { borderColor: '#FF9A56', color: '#FF9A56' } }}
                          >
                            Copiar
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Fade>
        )}

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ bgcolor: isDark ? '#2A1A20' : '#FFF5F8', color: '#9B59B6' }}>Crear Nueva Agenda 📅</DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Nombre de la Agenda"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
              {name && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', pl: 1 }}>
                  Su enlace será: <strong>/{business?.slug || businessId}/{name.toString().toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-').replace(/^-+/, '').replace(/-+$/, '') || 'agenda'}</strong>
                </Typography>
              )}
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>

                <Select
                  value={type}
                  label="Tipo"
                  onChange={(e) => setType(e.target.value)}
                >
                  {typeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Descripción"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                label="Capacidad"
                type="number"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                fullWidth
              />
              {error && <Alert severity="error">{error}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpen(false)} sx={{ color: isDark ? '#B0B0B0' : '#757575' }}>Cancelar</Button>
            <Button onClick={handleCreateBoard} variant="contained" sx={{ bgcolor: '#9B59B6', '&:hover': { bgcolor: '#8E44AD' } }}>Crear</Button>
          </DialogActions>
        </Dialog>

        <MassCancellationDialog
          open={massCancelOpen}
          onClose={() => setMassCancelOpen(false)}
          onConfirm={handleMassCancellation}
          boards={boards}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
            ¿Enviar a la papelera?
          </DialogTitle>
          <DialogContent>
            <Typography>
              El elemento se moverá a la papelera y podrá ser restaurado dentro de los próximos 30 días.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button onClick={handleDelete} color="error" variant="contained" startIcon={<Delete />}>
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

        <BusinessTour />
      </Container >
    </Box>
  );
};

export default BusinessDetail;